import { snakeCase } from 'lodash';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  ApprovalNotificationGroup,
  Notification as NotificationT,
  NotificationGroup,
  TaskNotificationGroup,
  UserTimesheetNotificationGroup,
} from 'api/notifications';
import { EventType } from 'api/notifications/meta';
import { TaskType } from 'api/tasks';
import { RoutePaths } from 'constant';
import { unreachable } from 'utils/unreachable';
import { Strong } from '../basics';
import { NotifySomeoneViaAutomationLineItem } from './automations/notify-someone-via-automation';
import { Causer } from './causer';
import { NoteCreatedView, NoteUpdatedView } from './customer-portfolio';
import { DependentTaskChangedView } from './dependent-task-changed';
import { DocumentChangedView } from './document-changed';
import { DocumentCreatedView } from './document-created';
import {
  ProjectBudgetThresholdReachedView,
  ProjectHoursThresholdReachedView,
} from './financials';
import { MessageRelatedView } from './msg-related';
import { PartnerCompanyAddedToProjectView } from './partner-company-added-to-project';
import { ProjectArchivedView } from './project-archived';
import {
  ProjectConversationMemberAddedView,
  ProjectConversationMemberInvitedView,
} from './project-conversation-events';
import { ProjectCsatAddedMessage } from './project-csat-added';
import { ProjectDeletedView } from './project-deleted';
import { ProjectDueDateChangedView } from './project-due-date-changed';
import { ProjectMemberAddedView } from './project-member-added';
import { ProjectMemberRemovedView } from './project-member-removed';
import { ProjectNameChangedView } from './project-name-changed';
import { ProjectPhaseAddedView } from './project-phase-added';
import { ProjectPhaseDueDateChangedView } from './project-phase-due-date-changed';
import { ProjectPhaseNameChangedView } from './project-phase-name-changed';
import { ProjectPhaseRemovedView } from './project-phase-removed';
import { ProjectPhaseStartDateChangedView } from './project-phase-start-date-changed';
import { ProjectPhaseStatusChangedView } from './project-phase-status-changed';
import { ProjectPlaceholderResolveView } from './project-placeholder-resolve';
import { ProjectStartDateChangedView } from './project-start-date-changed';
import { ProjectStatusChangedView } from './project-status-changed';
import { ProjectStatusUpdatePublishedView } from './project-status-update-published';
import { ProjectUnarchivedView } from './project-unarchived';
import { ResourceRequestApprovedView } from './resource-request/resource-request-approved-view';
import { ResourceRequestAssigneeChangedView } from './resource-request/resource-request-assignee-changed-view';
import { ResourceRequestAssigneeUpdatedView } from './resource-request/resource-request-assignee-updated-view';
import { ResourceRequestSetAssigneeView } from './resource-request/resource-request-set-assignee-view';
import { ResourceRequestStatusChangedView } from './resource-request/resource-request-status-changed-view';
import { TaskApprovalRequest } from './task-approval-request';
import { TaskAssignedToUserView } from './task-assigned';
import { TaskAssigneeChangedView } from './task-assignee-changed';
import { TaskAtRiskChangedView } from './task-at-risk-changed';
import { TaskAttachmentChangedView } from './task-attachment-changed';
import { TaskCreatedView } from './task-created';
import { TaskDueDateChangedView } from './task-due-date-changed';
import { TaskDueDateOverdueView } from './task-due-date-overdue';
import { TaskDueDateReminderView } from './task-due-date-reminder';
import { TaskEstimateChangedView } from './task-estimate-changed';
import { TaskFieldChangedView } from './task-field-changed';
import { TaskFormResponseView } from './task-form-response-added';
import { TaskNameOrDescriptionChangedView } from './task-name-or-description-changed';
import { TaskPriorityChangedView } from './task-priority-changed';
import { TaskPrivateNoteChangedView } from './task-private-note-changed';
import { TaskProgressChangedView } from './task-progress-changed';
import { TaskStartDateChangedView } from './task-start-date-changed';
import { TaskStartDateReminderView } from './task-start-date-reminder';
import { TaskStatusChangedView } from './task-status-changed';
import { TaskDependencyStatusChangedView } from './task/task-dependency-changed-view';
import { TimeoffCreatedView, TimeoffDeletedView } from './timeoff';
import {
  TimesheetApprovedView,
  TimesheetDueReminderView,
  TimesheetOverdueReminderView,
  TimesheetRequestedView,
  TimesheetSubmittedView,
} from './timesheet-events';
import { UnfinishedView } from './unfinished';
import { UserAddedToProjectView } from './user-added-to-project';

export const renderMessage = (
  group: NotificationGroup,
  { container, timestamp, meta }: NotificationT,
  open: () => void,
  setItem?: () => void,
) => {
  const id = `notifications.messages.${snakeCase(meta.eventType)}`;
  const isTaskWithFormInstance = Boolean(
    (group as TaskNotificationGroup).task?.formInstanceId,
  );
  const updateWorkspaceLocalStorage = () => {
    if (setItem) {
      setItem();
    }
  };

  switch (meta.eventType) {
    case EventType.TaskCreated:
      return (
        <TaskCreatedView
          meta={meta}
          open={open}
          isTaskWithFormInstance={isTaskWithFormInstance}
        />
      );
    case EventType.TaskAtRiskChanged:
      return (
        <TaskAtRiskChangedView
          meta={meta}
          isTaskWithFormInstance={isTaskWithFormInstance}
        />
      );
    case EventType.TaskNameOrDescriptionChanged:
      return (
        <TaskNameOrDescriptionChangedView
          meta={meta}
          isTaskWithFormInstance={isTaskWithFormInstance}
        />
      );
    case EventType.TaskPrivateNoteChanged:
      return (
        <TaskPrivateNoteChangedView
          meta={meta}
          isTaskWithFormInstance={isTaskWithFormInstance}
        />
      );
    case EventType.TaskStatusChanged:
      return (
        <TaskStatusChangedView
          type={(group as TaskNotificationGroup).task.type ?? TaskType.Task}
          meta={meta}
        />
      );
    case EventType.TaskDependencyStatusChanged:
      return (
        <TaskDependencyStatusChangedView
          type={(group as TaskNotificationGroup).task.type ?? TaskType.Task}
          meta={meta}
        />
      );
    case EventType.MessageCreated:
    case EventType.MessageReactionAdded:
    case EventType.MessageReplyAdded:
      if (meta.message?.parentAttachment?.attachmentId) {
        return (
          <FormattedMessage
            id="notifications.messages.attachment_comment_created"
            values={{
              causer: <Causer user={meta.by} />,
              attachment: (
                <Link
                  to={RoutePaths.attachmentWorkspace.replace(
                    ':attachmentId',
                    meta.message.parentAttachment.attachmentId.toString(),
                  )}
                  onClick={updateWorkspaceLocalStorage}
                >
                  <FormattedMessage id="view_annotation" />
                </Link>
              ),
            }}
          />
        );
      }

      return <MessageRelatedView id={id} meta={meta} />;
    case EventType.UserMentionedInMessage:
      if (meta.message?.parentAttachment?.attachmentId) {
        return (
          <FormattedMessage
            id="notifications.messages.attachmen_user_mentioned"
            values={{
              causer: <Causer user={meta.by} />,
              attachment: (
                <Link
                  to={RoutePaths.attachmentWorkspace.replace(
                    ':attachmentId',
                    meta.message.parentAttachment.attachmentId.toString(),
                  )}
                  onClick={updateWorkspaceLocalStorage}
                >
                  <FormattedMessage id="view_annotation" />
                </Link>
              ),
            }}
          />
        );
      }

      return (
        <FormattedMessage
          id={id}
          values={{
            causer: <Causer user={meta.by} />,
          }}
        />
      );
    case EventType.UserMentionedInDocument:
    case EventType.UserMentionedInTask:
    case EventType.ApprovalRequestApproved:
    case EventType.ApprovalRequestChangesRequested:
      return (
        <FormattedMessage
          id={id}
          values={{
            causer: <Causer user={meta.by} />,
          }}
        />
      );
    case EventType.TaskAssignedToUser:
      return (
        <TaskAssignedToUserView
          meta={meta}
          isTaskWithFormInstance={isTaskWithFormInstance}
        />
      );
    case EventType.TaskAssigneeChanged:
      return <TaskAssigneeChangedView meta={meta} />;
    case EventType.DependentTaskChanged:
      return <DependentTaskChangedView meta={meta} />;
    case EventType.TaskPriorityChanged:
      return <TaskPriorityChangedView meta={meta} />;
    case EventType.TaskEstimateChanged:
      return <TaskEstimateChangedView meta={meta} />;
    case EventType.TaskAttachmentChanged:
      return <TaskAttachmentChangedView meta={meta} />;
    case EventType.TaskFieldChanged:
      return <TaskFieldChangedView meta={meta} />;
    case EventType.TaskStartDateChanged:
      return <TaskStartDateChangedView meta={meta} />;
    case EventType.TaskDueDateChanged:
      return <TaskDueDateChangedView meta={meta} />;
    case EventType.TaskDueDateOverdue:
      return (
        <TaskDueDateOverdueView
          meta={meta}
          isTaskWithFormInstance={isTaskWithFormInstance}
        />
      );
    case EventType.TaskStartDateReminder:
      return (
        <TaskStartDateReminderView
          timestamp={timestamp}
          meta={meta}
          isTaskWithFormInstance={isTaskWithFormInstance}
        />
      );
    case EventType.TaskDueDateReminder:
      return (
        <TaskDueDateReminderView
          timestamp={timestamp}
          meta={meta}
          isTaskWithFormInstance={isTaskWithFormInstance}
        />
      );
    case EventType.TaskFormResponseChanged:
      return (
        <TaskFormResponseView
          meta={meta}
          projectId={(group as TaskNotificationGroup).task.project.id}
          formInstanceId={(group as TaskNotificationGroup).task.formInstanceId}
        />
      );
    case EventType.TaskProgressChanged:
      return <TaskProgressChangedView meta={meta} />;
    case EventType.DocumentCreated:
      return <DocumentCreatedView meta={meta} open={open} />;
    case EventType.DocumentChanged:
      return <DocumentChangedView meta={meta} />;
    case EventType.ProjectNameChanged:
      return <ProjectNameChangedView meta={meta} />;
    case EventType.ProjectStatusChanged:
      return <ProjectStatusChangedView meta={meta} />;
    case EventType.ProjectStartDateChanged:
      return <ProjectStartDateChangedView meta={meta} />;
    case EventType.ProjectDueDateChanged:
      return <ProjectDueDateChangedView meta={meta} />;
    case EventType.ProjectPhaseAdded:
      return <ProjectPhaseAddedView meta={meta} />;
    case EventType.ProjectPhaseRemoved:
      return <ProjectPhaseRemovedView meta={meta} />;
    case EventType.ProjectPhaseNameChanged:
      return <ProjectPhaseNameChangedView meta={meta} />;
    case EventType.ProjectPhaseStatusChanged:
      return <ProjectPhaseStatusChangedView meta={meta} />;
    case EventType.ProjectPhaseStartDateChanged:
      return <ProjectPhaseStartDateChangedView meta={meta} />;
    case EventType.ProjectPhaseDueDateChanged:
      return <ProjectPhaseDueDateChangedView meta={meta} />;
    case EventType.ProjectCsatAdded:
      return <ProjectCsatAddedMessage meta={meta} />;
    case EventType.ProjectStatusUpdatePublished:
      return (
        <ProjectStatusUpdatePublishedView
          meta={meta}
          projectId={parseInt(container.uri.replace('/projects/', ''), 10)}
        />
      );
    case EventType.UserAddedToProject:
      return <UserAddedToProjectView meta={meta} />;
    case EventType.ProjectMemberRemoved:
      return <ProjectMemberRemovedView meta={meta} />;
    case EventType.ProjectMemberAdded:
      return <ProjectMemberAddedView meta={meta} />;
    case EventType.ProjectPartnerAddition:
      return <PartnerCompanyAddedToProjectView meta={meta} />;
    case EventType.ProjectDeleted:
      return <ProjectDeletedView meta={meta} />;
    case EventType.ProjectArchived:
      return <ProjectArchivedView meta={meta} />;
    case EventType.ProjectUnarchived:
      return <ProjectUnarchivedView meta={meta} />;
    case EventType.ApprovalRequested:
      return (
        <TaskApprovalRequest
          meta={meta}
          approverIds={
            (group as ApprovalNotificationGroup).approval.approverIds
          }
        />
      );

    case EventType.ProjectConversationMemberAdded:
      return <ProjectConversationMemberAddedView meta={meta} />;
    case EventType.ProjectConversationMemberInvited:
      return <ProjectConversationMemberInvitedView meta={meta} />;

    case EventType.TimesheetDueReminder:
      return (
        <TimesheetDueReminderView
          meta={meta}
          userId={(group as UserTimesheetNotificationGroup).timesheet.userId}
        />
      );
    case EventType.TimesheetOverdueReminder:
      return (
        <TimesheetOverdueReminderView
          meta={meta}
          userId={(group as UserTimesheetNotificationGroup).timesheet.userId}
        />
      );
    case EventType.TimesheetRequested:
      return (
        <TimesheetRequestedView
          meta={meta}
          userId={(group as UserTimesheetNotificationGroup).timesheet.userId}
        />
      );
    case EventType.TimesheetApproved:
      return (
        <TimesheetApprovedView
          meta={meta}
          userId={(group as UserTimesheetNotificationGroup).timesheet.userId}
        />
      );
    case EventType.TimesheetSubmitted:
      return (
        <TimesheetSubmittedView
          meta={meta}
          userId={(group as UserTimesheetNotificationGroup).timesheet.userId}
        />
      );
    case EventType.MeetingNoteCreated:
    case EventType.MeetingNoteChanged:
    case EventType.TaskFollowerAdded:
      return <UnfinishedView meta={meta} />;
    case EventType.TaskPhaseChanged:
      return (
        <FormattedMessage
          id={
            isTaskWithFormInstance
              ? 'notifications.messages.form_task_phase_changed'
              : id
          }
          values={{
            causer: <Causer user={meta.by} />,
            hasFrom: Boolean(meta.from),
            from: meta.from && <Strong>{meta.from.value}</Strong>,
            to: <Strong>{meta.to.value}</Strong>,
          }}
        />
      );
    case EventType.ProjectFolderAttachmentAdded:
    case EventType.ProjectFolderAttachmentRemoved:
    case EventType.ProjectFolderAttachmentChanged: {
      const { by, addition, removal } = meta;

      if (addition?.attachments || removal?.attachments) {
        return (
          <FormattedMessage
            id={
              (addition || removal)!.attachments.length > 1
                ? 'notifications.messages.project_files_changed.multiple'
                : 'notifications.messages.project_files_changed.single'
            }
            values={{
              causer: <Causer user={by} />,
              title: (
                <Strong>{(addition || removal)!.attachments[0].name}</Strong>
              ),
              count: (addition || removal)!.attachments.length - 1,
              folderName: <Strong>{(addition || removal)!.folderName}</Strong>,
              action: addition != null ? 'added' : 'removed',
              preposition: addition != null ? 'to' : 'from',
            }}
          />
        );
      }

      return null;
    }
    case EventType.TimeOffCreated:
      return <TimeoffCreatedView meta={meta} />;
    case EventType.TimeOffDeleted:
      return <TimeoffDeletedView meta={meta} />;
    case EventType.ProjectHoursThresholdReached:
      return <ProjectHoursThresholdReachedView meta={meta} />;
    case EventType.ProjectBudgetThresholdReached:
      return <ProjectBudgetThresholdReachedView meta={meta} />;
    case EventType.ProjectPlaceholderResolve:
      return <ProjectPlaceholderResolveView meta={meta} />;
    case EventType.NotifyThroughAutomation:
      return <NotifySomeoneViaAutomationLineItem meta={meta} />;
    case EventType.NoteAdded:
      return <NoteCreatedView meta={meta} />;
    case EventType.NoteUpdated:
      return <NoteUpdatedView meta={meta} />;
    case EventType.ResourceRequestStatusChanged:
      return <ResourceRequestStatusChangedView meta={meta} />;
    case EventType.ResourceRequestApproved:
      return <ResourceRequestApprovedView meta={meta} />;
    case EventType.ResourceRequestSetAssignee:
      return <ResourceRequestSetAssigneeView meta={meta} />;
    case EventType.ResourceRequestAssigneeChanged:
      return <ResourceRequestAssigneeChangedView meta={meta} />;
    case EventType.ResourceRequestAssigneeUpdated:
      return <ResourceRequestAssigneeUpdatedView meta={meta} />;
    default:
      return unreachable(meta);
  }
};
